/*global define*/
/*jshint devel:true*/
/**
 * Base View backbone class
 */
define(
	'BaseView',[
		'backbone',
		'underscore',
		'jquery',
		'rivets',
		'BaseClassGenerator',
		'underscoreString'
	],
	function (Backbone, _, $, rivets, BaseClassGenerator, str) {
		"use strict";

		//DEBUG
		//console.log('Call Settings config view');

		//////////////////////////////////////////////////////////////////////////////////////////////////////////////////
		// PRIVATE FUNCTIONS
		//////////////////////////////////////////////////////////////////////////////////////////////////////////////////
		/**
		 * List of option keys to ignore when copying the View options parameter onto the View class instance
		 * @type {Object}
		 */
		var ignoredOptionNames = {
			model: null,
			collection: null,
			el: null,
			id: null,
			className: null,
			tagName: null,
			attributes: null
		};

		/**
		 * @todo make this a private function
		 * @param stateDefStr
		 * @return {{nextStates: Array, immediateNextState: String}}
		 */
		var parseStateDef = function (stateDefStr) {
			var immediateNextState = null,
				nextStates = [],
				stateList = stateDefStr && $.trim(stateDefStr).split(/\s+/g);

			if (stateList && stateList.length) {
				$.each(stateList, function (i, stateToken) {
					var matches = stateToken.match(/^(\w+)(:next)*$/),
						stateId = matches && matches[1],
						isNextState = matches && matches[2];

					if (!stateId) { // if the stateId is invalid,
						throw new Error('parseStateDef(): invalid state token "' + stateToken + '"');
					}

					nextStates.push(stateId); // add the state id to the list

					if (isNextState) { // if true, it contains ":next"
						immediateNextState = stateId;
					}
				});
			}

			return {
				nextStates: nextStates,
				immediateNextState: immediateNextState
			};
		};
		//////////////////////////////////////////////////////////////////////////////////////////////////////////////////
		// CLASS DEFINITION
		//////////////////////////////////////////////////////////////////////////////////////////////////////////////////
		var ParentClass = Backbone.View,
			parentClassPrototype = ParentClass.prototype;

		/**
		 * @class
		 * @name BaseView
		 * @extends Backbone.View
		 */
		return BaseClassGenerator.generate(ParentClass,
			/**
			 * @lends BaseView.prototype
			 */
			{
				//////////////////////////////////////////////////////////////////////////////////////////////////////////////////
				// MEMBERS
				//////////////////////////////////////////////////////////////////////////////////////////////////////////////////
				/**
				 * Name of the current class. Used to build debug messages.
				 * @type {String}
				 */
				className: 'BaseView',
				/**
				 * Rivets internal view
				 * @type {Rivets.View}
				 */
				rivetsView: null,
				/**
				 * The rivets prefix used to isolate data-* attributes
				 * @warning This is unsupported at the moment
				 * @type {String}
				 */
				// rivetsPrefix: '',

				/**
				 * Reference to the View's controller
				 * @BaseController
				 */
				controller: null,
				/**
				 * List of dom selectors to use with jQuery.
				 * The jQuery selector range is limited to the view root element (this.el)
				 * They'll only be processed by the render method
				 * @example
				 * {
				 *  buttons: 'button',
				 *  formLinks: 'form a[href]'
				 * }
				 * DOM elements found after processing these selectors are stored in this.domElements.
				 * @type {Object} A hash of jQuery selectors
				 */
				domSelectors: null,
				/**
				 * Contains a list of dom elements found after processing the this.domSelectors property.
				 * This property is set in render()
				 * @example
				 * {
				 *  buttons: jQuery_obj, // $('button', this.el)
				 *  formLinks: jQuery_obj2, // $('form a[href]', this.el)
				 * }
				 * @type {Object} A hash of jQuery elements
				 */
				domElements: null,
				/**
				 * Current state id
				 * @type {String}
				 */
				state: null,
				/**
				 * Initial state at object time
				 * @type {String}
				 */
				initState: 'init',
				/**
				 * States and transition definitions
				 * @type {Object}
				 */
				states: null,
				/**
				 *
				 * @type {String}
				 */
				_nextState: null,
				/**
				 * Default states and transition definitions
				 * @type {Object}
				 */
				defaultStates: {
					// 'stateName': 'allowedNextState1 allowedNextState2',
					// 'stateName': 'allowedNextState1:next allowedNextState2',
					// 'stateName': {{nextStates: Array, immediateNextState: String}},
					init: '', // default init state
					reset: 'init:next', // all states can go to the reset state, so it's not necessary to specify this in other state transitions
					error: '' // all states can go to the error state, so it's not necessary to specify this in other state transitions
				},
				/**
				 *
				 */
				nextImmediateStateTransitionCallbackFc: null,
				/**
				 *
				 * @type {string}
				 */
				stateTransitionPhase: null,
				leaveStatePhaseName: 'leave',
				enterStatePhaseName: 'enter',
				leaveStateEventName: 'leaveState',
				enterStateEventName: 'enterState',
				//////////////////////////////////////////////////////////////////////////////////////////////////////////////////
				// METHODS
				//////////////////////////////////////////////////////////////////////////////////////////////////////////////////
				/**
				 * Init the view
				 * @constructs
				 * @param {Object} options
				 */
				initialize: function (options) {
					var self = this,
						methodName = 'initialize';

					//DEBUG
					//console.group(self.report(methodName, '') + ' start: ', self);

					// Extend some options onto the class instance directly
					if (options) {
						$.each(options, function (optionName, optionValue) {
							if (!(optionName in ignoredOptionNames)) {
								self[optionName] = optionValue;
							}
						});
					}

					// run parent method
					parentClassPrototype.initialize.apply(self, [].slice.call(arguments));

					if (options && options.controller) {
						self.controller = options.controller;
					}

					// extend custom states onto default states
					self.states = $.extend({}, self.defaultStates, self.states);

					self.parseStates().setupStateChangeHandlers();

					//DEBUG
					//console.log( self.report(methodName, '') + 'this.states = %o', self.states);

					//DEBUG
					//console.groupEnd();

					//DEBUG
					//self.on('all', function (event, model, value, options) {
					//	//DEBUG
					//	console.log('view evt()', arguments);
					//});

					return self;
				},
				setupDomElements: function () {
					var self = this,
						domRoot = self.el,
						domSelectors = self.domSelectors,
						domElements = self.domElements;

					if (!domElements) {
						domElements = self.domElements = {};
					}

					if (domSelectors) {
						$.each(domSelectors, function (key, selector) {
							// limit selector scope to the domRoot
							domElements[key] = $(selector, domRoot);
						});
					}
					return self;
				},
				/**
				 * Renders the view
				 * - processes the domSelectors and store the results in
				 * @return {Backbone.View}
				 */
				render: function () {
					var self = this;

					self.setupDomElements()
						.startInitState(); // start state at rendition time

					// return result of the parent method
					return parentClassPrototype.render.apply(self, [].slice.call(arguments));
				},
				/**
				 * Starts the state machine by entering the initState.
				 * If the machine has already started, we will go through the 'reset' state beforehand.
				 * @return {*}
				 */
				startInitState: function () {
					var self = this;
					if (self.state) { // if we are already in some state
						self.resetState(); // reset state, it'll go to the initial state after
					} else { // or go to initial state
						self.setState(self.initState);
					}
					return self;
				},
				/**
				 * @todo make this a private function
				 */
				parseStates: function () {
					var self = this,
						methodName = 'parseStates',
						states = self.states;

					if (states) {

						if (!$.isPlainObject(states) || $.isArray(states)) {
							throw new Error(self.report(methodName, 'states definition object must be a plain object.'));
						}

						// process each state definition
						//var allNextStates = [];
						$.each(states, function (stateId, stateDef) {
							stateDef = $.isPlainObject(stateDef) ? stateDef : parseStateDef(stateDef || '');

							states[stateId] = stateDef;

							var nextStates = stateDef.nextStates,
								uniqueNextStates = _.uniq(nextStates);

							if (nextStates.length !== uniqueNextStates.length) { // if we have non-unique state names
								//DEBUG
								//console.log('nextStates = ', nextStates);
								//console.log('uniqueNextStates = ', uniqueNextStates);
								throw new Error(self.report(methodName, 'the list of next states defined in "' + stateId + '" contains duplicates.'));
							}

							//allNextStates = allNextStates.concat(nextStates);
						});

						// search for unknown state ids
						$.each(states, function (stateId, stateDef) {
							$.each(stateDef.nextStates, function (i, nextStateId) {
								if (!states[nextStateId]) {
									throw new Error(self.report(methodName, 'the state "' + nextStateId + '" defined in the next state list of state "' + stateId + '" is unknown.'));
								}
							});
						});
					}
					return self;
				},
				/**
				 * Setup state change handlers.
				 * They will trigger calls to enterState and leaveState handlers on the current object.
				 * It will also set a CSS class on the root DOM element of the current view object.
				 * CSS class format is: <stateId>_state. e.g. init_state, idle_state, etc...
				 * @return {*}
				 */
				setupStateChangeHandlers: function () {
					var self = this,
						leaveStateEventName = self.leaveStateEventName,
						enterStateEventName = self.enterStateEventName,
						leaveHandlerPrefix = 'after',
						enterHandlerPrefix = 'when',
						methodName = 'setupStateChangeHandlers';

					//throw new Error( self.report(methodName, 'states definition object must be a plain object.') );

					// State change handler
					var stateChangeHandler = function (prefix, stateId, obj) {
						var handlerFcName = prefix + str.capitalize(stateId),
							handlerFc = obj[ handlerFcName ];

						//DEBUG
						//console.log(self.report(methodName, 'handlerFcName = %o'), handlerFcName);
						//console.log(self.report(methodName, 'handlerFc = %o'), handlerFc);

						if (self.el) { // if we have a root dom element, set the state in a css class name.
							self.$el[ (prefix === enterHandlerPrefix) ? 'addClass' : 'removeClass' ](stateId + '_state');
						}

						if ($.isFunction(handlerFc)) {
							handlerFc.apply(obj, [].slice.call(arguments).slice(2));
						}
					};

					// for each defined state
					$.each(self.states, function (stateId, stateDef) {

						self.bind(
							leaveStateEventName + ':' + stateId,
							_.bind(stateChangeHandler, self, leaveHandlerPrefix, stateId) // @see http://underscorejs.org/#bind
						);

						self.bind(
							enterStateEventName + ':' + stateId,
							_.bind(stateChangeHandler, self, enterHandlerPrefix, stateId) // @see http://underscorejs.org/#bind
						);

					});

					return self;
				},
				/**
				 *
				 * @param stateId
				 * @return {*}
				 */
				getStateDef: function (stateId) {
					var self = this,
						methodName = 'getStateDef',
						stateDef = self.states[stateId];

					if (!stateDef) {
						throw new Error(self.report(methodName, 'Unknown state "' + stateId + '"'));
					}

					return stateDef;
				},
				/**
				 * Get the state of this view.
				 * @return {String}
				 */
				getState: function () {
					return this.state;
				},
				/**
				 * Set the state of this view.
				 * @param {String} targetStateId
				 * @return {BaseView}
				 */
				setState: function (targetStateId) {
					var self = this,
						methodName = 'setState',
					//targetState = self.getStateDef(targetStateId),
						curStateId = self.state,
						leaveStatePhaseName = self.leaveStatePhaseName,
						enterStatePhaseName = self.enterStatePhaseName,
						leaveStateEventName = self.leaveStateEventName,
						enterStateEventName = self.enterStateEventName,
						curState,
						allowedNextStates;

					//DEBUG
					//console.group(self.report(methodName, "%o -> %o"), curStateId, targetStateId);

					// do not allow state changes during the "leave" state phase
					// This is to limit the variety of use cases to manage state changes in applications
					if (self.stateTransitionPhase === leaveStatePhaseName) {
						throw new Error(self.report(methodName, 'changing state during the "' + leaveStatePhaseName + '" phase is forbidden'));
					}

					// if we are not actually changing of state, do nothing
					if (targetStateId === curStateId) {

						//DEBUG
						//console.groupEnd();

						return self;
					}

					if (curStateId !== null) { // if we've started being in a state, as opposed to just start this object lifecycle
						curState = self.getStateDef(curStateId);
						allowedNextStates = curState.nextStates;

						if (($.inArray(targetStateId, allowedNextStates) === -1) &&
							(
								(targetStateId !== 'error') &&
									(targetStateId !== 'reset')
								)
							) { // if we're not allowed to go to that next state
							throw new Error(self.report(methodName, 'Next target state "' + targetStateId + '" is not allowed from current state "' + curStateId + '"'));
						}
					}

					// if we're currently processing the state enter phase, so we'll post-pone this state change
					if (self.stateTransitionPhase === enterStatePhaseName) {
						//DEBUG
						//console.log(self.report(methodName, "we're currently processing the state enter phase"));

						self._nextState = targetStateId;
						self.nextImmediateStateTransitionCallbackFc = null; // reset the next state change callback

						//DEBUG
						//console.groupEnd();

						return self;
					}

					// notify other programs we're leaving the current state
					self.runStateTransition(leaveStatePhaseName, function () {
						//DEBUG
						//console.group(self.report(methodName, "%o* -> %o: leaving current state"), curStateId, targetStateId);

						var leaveStateDetailedEventName = leaveStateEventName + ':' + curStateId;

						// bind this code to leaveStateDetailedEventName so that we run the general "leave state event" only after (and respect the event chain too)
						self.once(leaveStateDetailedEventName, function () {
							// callback signature is (BaseView, leaveStateId, nextStateId, options) - options can be used as a bag to transmit data between event handlers
							self.trigger(leaveStateEventName, self, curStateId, targetStateId, {});
						});

						// callback signature is (BaseView, leaveStateId, nextStateId, options) - options can be used as a bag to transmit data between event handlers
						self.trigger(leaveStateDetailedEventName, self, curStateId, targetStateId, {});

						//DEBUG
						//console.groupEnd();
					});

					// now, we're allowed to go to the next state
					self.state = targetStateId;
					self._nextState = null;

					//DEBUG
					//console.log(self.report(methodName, "%o -> %o"), curStateId, targetStateId);

					self.runStateTransition(enterStatePhaseName, function () {
						var enteredStateDetailedEventName = enterStateEventName + ':' + targetStateId;

						// notify other programs we've entered the new state
						//DEBUG
						//console.group(self.report(methodName, "%o -> %o*: entered the new state"), curStateId, targetStateId);

						// bind an event handler to move to the next immediate state,
						// it'll be the last one to be called - but the event flow might be stopped before we reach it.
						self.setupNextImmediateStateTransition(enterStateEventName);

						// bind this code to enteredStateDetailedEventName so that we run the general "enter state event" only after (and respect the event chain too)
						self.once(enteredStateDetailedEventName, function () {

							// callback signature is (BaseView, enteredStateId, previousStateId, options) - options can be used as a bag to transmit data between event handlers
							self.trigger(enterStateEventName, self, targetStateId, curStateId, {});
						});
						// callback signature is (BaseView, enteredStateId, previousStateId, options) - options can be used as a bag to transmit data between event handlers
						self.trigger(enteredStateDetailedEventName, self, targetStateId, curStateId, {});

						//DEBUG
						//console.groupEnd();
					});

					// if a next state was set during the "enter" phase, process it
					if (self._nextState) {
						//DEBUG
						//console.log(self.report(methodName, "a next state was set during the 'enter' phase, process it"));
						self.setState(self._nextState);
					}

					//DEBUG
					//console.groupEnd();

					return self;
				},
				/**
				 *
				 * @param phase
				 * @param callbackFc
				 * @return {*}
				 */
				runStateTransition: function (phase, callbackFc) {
					var self = this;
					self.stateTransitionPhase = phase;
					callbackFc(phase);
					self.stateTransitionPhase = null;
					return this;
				},
				/**
				 *
				 * @param eventName
				 * @return {*}
				 */
				setupNextImmediateStateTransition: function (eventName) {
					var self = this,
						methodName = 'setupNextImmediateStateTransition';

					//DEBUG
					//console.log( self.report(methodName, 'eventName = %o'), eventName);

					// bind an event handler to move to the next immediate state,
					// it'll be the last one to be called - but the event flow might be stopped before we reach it.
					var callbackFc = function (baseViewObj, enteredStateId, previousStateId, options) {
						// if we still have the same callback function, that means there was no prior attempt to change state before this callback function is run
						if (self.nextImmediateStateTransitionCallbackFc === callbackFc) {
							//DEBUG
							//console.log( self.report(methodName, 'checking the next state: enteredStateId = %o, previousStateId = %o'), enteredStateId, previousStateId);

							var curState = baseViewObj.getStateDef(enteredStateId),
								immediateNextState = curState.immediateNextState;

							if (immediateNextState) {
								//DEBUG
								//console.log( self.report(methodName, 'moving to the next state: immediateNextState = %o'), immediateNextState);

								baseViewObj.setState(immediateNextState);
							}
						}
						//else {
						//DEBUG
						//console.log( self.report(methodName, 'not going to next state because another state was set before: enteredStateId = %o, previousStateId = %o'), enteredStateId, previousStateId);
						//}
					};

					// store callback function
					self.nextImmediateStateTransitionCallbackFc = callbackFc;

					//DEBUG
					//console.warn('change nextImmediateStateTransitionCallbackFc', callbackFc);

					self.once(eventName, callbackFc);
					return self;
				},
				resetState: function () {
					var self = this,
						methodName = 'resetState',
						states = self.states,
						resetStateId = 'reset';

					self.gotoState(resetStateId);
				},
				/**
				 * Binds rivets to the current View container (this.$el) and uses the given rivetsData as rivet's data source.
				 * The rivets internal view is stored in this.rivetsView
				 * @triggers rivets_bind Triggers the 'rivets_bind' event with the following arguments: {Rivets view DOM element}, {rivets data model}
				 * @param {Object} rivetsData
				 * @return {Backbone.View}
				 */
				bindRivets: function (rivetsData) {
					var self = this,
						methodName = 'bindRivets',
						rivetsView = self.rivetsView;

					if (!rivetsData) {
						throw new Error(self.report(methodName, 'rivetsData is empty or undefined.'));
					}

					if (rivetsView) { // if existing bindings exist, remove them before
						self.unbindRivets();
					}

					// add the controller instance to rivets data
					//rivetsData.controller = self.controller;

					self.rivetsView = rivets.bind(self.$el, rivetsData, {
						prefix: self.rivetsPrefix || ''
					});
					self.trigger('rivets_bind', self.$el, rivetsData);

					return self;
				},
				/**
				 * Unbind rivets that associated to the current view.
				 * @triggers rivets_unbind Triggers the 'rivets_unbind' event with the following arguments: {rivets view object}
				 */
				unbindRivets: function () {
					var self = this,
						rivetsView = self.rivetsView;

					if (rivetsView) {
						rivetsView.unbind();
					}
					self.trigger('rivets_unbind', rivetsView);

					return self;
				},

				formatAmount: function (amount) {
					if (typeof amount === 'object' && ('amount' in amount)) {
						if (amount.unit === "DOLLAR") {
							amount = amount.amount;
						} else {
							amount = amount.amount / 100;
						}
					}

					if (typeof amount === 'string') { // cast to number if it's a string
						amount = parseFloat(amount);
					}

					if (amount === null) {
						// check jira oe-137
						amount = 0;
					}

					if (amount === null || typeof amount !== 'number' || isNaN(amount)) {
						return 'formatAmount(): invalid input param';
					}

					if (amount < 0) {
						amount = -amount;
						return "$" + amount + " CR";
					}
					return "$" + amount.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,");
				},

				formatDate: function (date) {
					var padToTwo = function (number) {
						if (number <= 99) {
							number = ("0" + number).slice(-2);
						}
						return number;
					};
					if (date === null) {
						date = 0;
						console.error('the date should not be null');
					}
					var a = new Date(date);
					return padToTwo(a.getDate()) + "/" + padToTwo((a.getMonth() + 1)) + "/" + a.getFullYear();
				},

				byteConversion: function (value, toSmallest){
					var convertedValue = null;
					if (toSmallest == 0){
						if (value >= 1073741824){
							convertedValue = Math.round((value/1073741824))+" GB";
						} else {
							convertedValue = Math.round((value/1048576))+" MB";
						}
					} else {
						convertedValue = Math.round((value/1048576))+" MB";
					}
					return convertedValue;
				},

				formatDollar: function (value) {
					return "$" + value;
				},

				/**
				 * Removes a view from the DOM, and calls stopListening to remove any bound events that the view has listenTo'd.
				 * This overloaded method also removes the Rivets view bindings.
				 * @return {Backbone.View}
				 */
				remove: function () {
					var self = this;
					self.unbindRivets();
					return parentClassPrototype.remove.call(self, [].slice.call(arguments));
				}
			}
		);
	}
);

